import React, { useState, useMemo } from 'react';
import Classes from './EmailInvoicePopup.module.css';
import { motion } from 'framer-motion';
import { CircularProgress } from '@mui/material';
import backend from '../../../../../../../api';

function isEmail(v = '') {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());
}

/**
 * Props:
 * - handleClose: () => void
 * - invoiceId: string (required)
 * - defaultEmail?: string (prefills input)
 * - clients?: string[] (optional chips, purely cosmetic in this modal)
 */
export default function EmailInvoicePopup({ handleClose, invoiceId, defaultEmail = '', clients = [] }) {
  const [email, setEmail] = useState(defaultEmail);
  const [selectedClient, setSelectedClient] = useState(clients[0] || '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const canSend = useMemo(() => isEmail(email) && !!invoiceId && !sending, [email, invoiceId, sending]);

  const send = async () => {
    setError('');
    if (!invoiceId) {
      setError('Missing invoice id.');
      return;
    }
    if (!isEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    try {
      setSending(true);
      await backend.post(`/api/invoices/${invoiceId}/share`, { to: email });
      // Optional: toast here
      alert('Invoice emailed successfully.');
      handleClose?.();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to send invoice.';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className={Classes.popup}
      onClick={handleClose}
    >
      <motion.div
        initial={{ y: -7 }}
        animate={{ y: 0 }}
        exit={{ y: 7 }}
        transition={{ duration: 0.25 }}
        className={Classes.card}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={Classes.close} onClick={handleClose}>
          <svg xmlns="http://www.w3.org/2000/svg" height="25" fill="white" viewBox="0 0 384 512">
            <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/>
          </svg>
        </div>

        <div className={Classes.bgPopup}>
          <div />
          <h5 className={Classes.title}>Share Invoice</h5>
          <div className={Classes.curveCon} />
        </div>

        <div className={Classes.cardCon}>
          {/* Optional client chips */}
          {clients?.length ? (
            <div className={Classes.flexCol1}>
              <div className="flexAlign10">
                <h5 className={Classes.label}>Client</h5>
                <div className="optionalText"><h5>Optional</h5></div>
              </div>
              <select
                className={Classes.inputField}
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
              >
                {clients.map((val, ind) => (
                  <option key={ind} value={val}>{val}</option>
                ))}
              </select>
            </div>
          ) : null}

          {/* Email field */}
          <div className={Classes.flexCol1}>
            <label className={Classes.label}>Email</label>
            <input
              type="email"
              placeholder="john.smith@example.com"
              className={Classes.inputField}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
            {error ? <div className={Classes.error}>{error}</div> : null}
          </div>

          {/* Actions */}
          <div className={Classes.flexAlign}>
            <button
              type="button"
              onClick={handleClose}
              className={Classes.btn1}
              style={{ border: `1px solid var(--secondary-color)` }}
              disabled={sending}
            >
              <h5>Cancel</h5>
            </button>

            <button
              type="button"
              className={Classes.btn2}
              onClick={send}
              disabled={!canSend}
            >
              {sending ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CircularProgress size={16} />
                  <h5>Sending…</h5>
                </div>
              ) : (
                <h5>Share invoice</h5>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
